<template>
  <div
    class="enhanced-editor-container"
    :class="{ 'enhanced-editor-container--sticky-toolbar': stickyToolbar }"
  >
    <vue-editor
      :id="id"
      ref="vueEditor"
      :placeholder="placeholder"
      :editor-toolbar="customToolbar"
      @input="onEditorInput"
      @ready="onEditorReady"
    />
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';

const BlockEmbed = Quill.import('blots/block/embed');
const Parchment = Quill.import('parchment');

// Marca de "pertenece al template X" para bloques editables. A diferencia de
// TemplateContainer (un BlockEmbed opaco), esto es un Attributor a nivel de bloque,
// igual que align/indent: solo agrega data-template-id al nodo que YA es (p, h1-h6,
// li, blockquote) sin reemplazar su tipo. Por eso compone limpio con cualquier
// otro formato de bloque y el toolbar de Quill funciona adentro sin código adicional.
const TEMPLATE_ID_DOM_ATTR = 'data-template-id';
const TEMPLATE_NON_DELETABLE_DOM_ATTR = 'data-template-non-deletable';

const TemplateGroupAttributor = new Parchment.Attributor.Attribute(
  'templateGroup',
  TEMPLATE_ID_DOM_ATTR,
  { scope: Parchment.Scope.BLOCK }
);
Quill.register(TemplateGroupAttributor, true);

// Marca de bloque protegido de borrado dentro de un template editable
const TemplateProtectedAttributor = new Parchment.Attributor.Attribute(
  'templateProtected',
  TEMPLATE_NON_DELETABLE_DOM_ATTR,
  { scope: Parchment.Scope.BLOCK }
);
Quill.register(TemplateProtectedAttributor, true);

// Replica local del helper `applyFormat` de quill/modules/clipboard.js: agrega
// {[format]: value} a los attributes de cada op del delta.
const applyTemplateGroup = (delta, templateId, isNonDeletable = false) => {
  return delta.reduce((acc, op) => {
    if (op.attributes && op.attributes.templateGroup) {
      return acc.push(op);
    }
    const attrs = { ...op.attributes, templateGroup: templateId };
    if (isNonDeletable) {
      attrs.templateProtected = 'true';
    }
    return acc.insert(op.insert, attrs);
  }, new (Quill.import('delta'))());
};

// El backend necesita encontrar <div template-id="X"> literal en el HTML
// guardado (para reconstruir/auditar el template), así que hay que rearmar
// ese wrapper antes de emitir el HTML — el parseo (clipboard matcher de
// arriba) ya hace el camino inverso al cargar.
//
// Una lista (<ol>/<ul>) es un caso especial: el atributo vive en cada <li>,
// no en el contenedor. Si el template no cubre TODA la lista, hay que
// partirla en dos listas del mismo tipo, cada una con sus <li> contiguos.
const splitListByTemplateId = (listEl) => {
  const groups = [];
  Array.from(listEl.children).forEach(li => {
    const templateId = li.getAttribute(TEMPLATE_ID_DOM_ATTR);
    const isNonDeletable = li.getAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR) === 'true';
    li.removeAttribute(TEMPLATE_ID_DOM_ATTR);
    li.removeAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR);
    const last = groups[groups.length - 1];
    if (last && last.templateId === templateId) {
      last.items.push(li);
    } else {
      groups.push({ templateId, isNonDeletable, items: [li] });
    }
  });

  if (groups.length <= 1) {
    return [{
      templateId: groups.length ? groups[0].templateId : null,
      isNonDeletable: groups.length ? groups[0].isNonDeletable : false,
      node: listEl,
    }];
  }

  return groups.map(({ templateId, isNonDeletable, items }) => {
    const listClone = listEl.cloneNode(false);
    items.forEach(li => listClone.appendChild(li));
    return { templateId, isNonDeletable, node: listClone };
  });
};

// Recorre los hijos directos del editor y los agrupa en "unidades": cada una
// es o bien un nodo suelto (sin template) o un nodo (posiblemente una lista
// ya partida por splitListByTemplateId) marcado con un template-id.
const buildTemplateUnits = (container) => {
  const units = [];
  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      units.push({ templateId: null, isNonDeletable: false, node });
      return;
    }
    if (node.tagName === 'OL' || node.tagName === 'UL') {
      if (node.hasAttribute(TEMPLATE_ID_DOM_ATTR)) node.removeAttribute(TEMPLATE_ID_DOM_ATTR);
      if (node.hasAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR)) node.removeAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR);
      splitListByTemplateId(node).forEach(unit => units.push(unit));
      return;
    }
    const templateId = node.getAttribute(TEMPLATE_ID_DOM_ATTR);
    const isNonDeletable = node.getAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR) === 'true';
    if (templateId) node.removeAttribute(TEMPLATE_ID_DOM_ATTR);
    if (node.hasAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR)) node.removeAttribute(TEMPLATE_NON_DELETABLE_DOM_ATTR);
    units.push({ templateId: templateId || null, isNonDeletable, node });
  });
  return units;
};

// Un mismo template-id puede aparecer NO contiguo en el documento.
// Por eso se usa un Map en vez de agrupar solo por contigüidad: cada
// template-id reutiliza el MISMO <div> sin importar cuántas veces
// reaparezca más adelante, así nunca hay dos <div template-id="X"> y
// nada queda huérfano fuera de su template.
const wrapTemplateGroups = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const units = buildTemplateUnits(container);
  const result = document.createElement('div');
  const wrappersByTemplateId = new Map();

  units.forEach(({ templateId, isNonDeletable, node }) => {
    if (!templateId) {
      result.appendChild(node);
      return;
    }

    let wrapper = wrappersByTemplateId.get(templateId);
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.setAttribute('template-id', templateId);
      wrapper.setAttribute('data-non-deletable', isNonDeletable ? 'true' : 'false');
      wrapper.setAttribute('contenteditable', 'true');
      wrappersByTemplateId.set(templateId, wrapper);
      result.appendChild(wrapper);
    } else if (isNonDeletable && wrapper.getAttribute('data-non-deletable') !== 'true') {
      wrapper.setAttribute('data-non-deletable', 'true');
    }
    wrapper.appendChild(node);
  });

  return result.innerHTML;
};

// El backend usa varios atributos HTML personalizados, sin valor, como
// marcadores de negocio que sigue buscando en el HTML YA GUARDADO (no solo
// al generarlo la primera vez) — ver CompuMinuteEditorContentEngine.cs:
//  - custom-roman-section-counter / custom-agreements-reached-section-counter:
//    PopulateRomanSectionsNumber / PopulateAgreementsReachedSectionsNumber
//    buscan estos <span> por XPath y reemplazan su innerHTML con el número
//    correspondiente cada vez que se reconstruye el contenido.
//  - table-check / check-{propósito}: el mismo patrón para las casillas de
//    propósito del header.
//  - req-{propósito}: ResolveMinuteContentAsync busca este atributo en <p>/
//    <li> para decidir si esa sección condicional sigue vigente según los
//    propósitos seleccionados.
// Quill descarta cualquier atributo que no esté registrado como Attributor
// al convertir HTML a Delta, así que sin este mecanismo estos marcadores
// desaparecen para siempre en el primer guardado.
//
// Los contadores/checkbox los recalcula el backend por completo cada vez
// (siempre pisan el innerHTML entero), así que el usuario nunca los edita:
// se preservan como embed inline opaco, tengan o no texto placeholder
// adentro ahora mismo. `req-*` en cambio marca metadata sobre un párrafo
// que SÍ es contenido editable real del usuario.
//
// data-placeholder-field entra en la MISMA lista por un motivo distinto: no
// es un marcador del backend, es contenido que el usuario completa, pero
// tiene que ser un tag protegido (no editable directamente, no partible con
// Enter, no replicable seleccionando solo un pedazo) — solo se resuelve
// mediante el click-to-edit de más abajo (beginPlaceholderReplacement),
// que manipula el DOM directo y no le importa si el nodo detrás es un embed.
const OPAQUE_MARKER_ATTR_PATTERNS = [
  /^custom-roman-section-counter$/i,
  /^custom-agreements-reached-section-counter$/i,
  /^table-check$/i,
  /^check-/i,
  /^data-placeholder-field$/i,
];
const EDITABLE_MARKER_ATTR_PATTERNS = [/^req-/i];

const hasMatchingAttr = (node, patterns) => {
  if (!node.attributes) return false;
  return Array.from(node.attributes).some(attr => patterns.some(re => re.test(attr.name)));
};
const getMatchingAttrNames = (node, patterns) => {
  if (!node.attributes) return [];
  return Array.from(node.attributes)
    .map(attr => attr.name)
    .filter(name => patterns.some(re => re.test(name)));
};

// Envoltorio interno (ver expandCustomMarkerAttrs, que lo revierte a
// atributos reales antes de guardar): nombres de marcador separados por
// espacio, aplicados como un único Attributor de bloque — mismo patrón que
// templateGroup, para no reemplazar el tipo del bloque que los lleva.
const CUSTOM_MARKER_ATTRS_DOM_ATTR = 'data-custom-marker-attrs';
const CustomMarkerAttrsAttributor = new Parchment.Attributor.Attribute(
  'customMarkerAttrs',
  CUSTOM_MARKER_ATTRS_DOM_ATTR,
  { scope: Parchment.Scope.BLOCK }
);
Quill.register(CustomMarkerAttrsAttributor, true);

// Marca del span "bookmark" que reemplaza temporalmente a un placeholder
// mientras se lo está reemplazando (ver beginPlaceholderReplacement más
// abajo). Es un Attributor a nivel INLINE registrado a propósito: sin él,
// el bookmark es un <span> sin ningún formato reconocido por Quill, y el
// propio MutationObserver interno de Quill lo desenvuelve (unwrap) como
// wrapper redundante la primera vez que reconcilia el árbol — algo
// asíncrono e independiente de cualquier código nuestro, que pasa solo
// con que transcurra un instante real (exactamente lo que pasa siempre
// entre "click en el placeholder" y "el usuario hace lo que sea después").
// Una vez desenvuelto, el nodo bookmark deja de estar en el DOM
// (isConnected pasa a false) sin que dispare ningún evento propio, así
// que restorePlaceholderEmbed no tiene forma de reaccionar: el texto
// queda ahí, plano, sin protección — exactamente el síntoma reportado
// ("pierde la protección"), tanto adentro como afuera de un template
// (el bug no distingue eso, así que tampoco lo hace este fix). Con el
// atributo registrado como formato real, Quill reconoce el nodo como un
// blot válido y lo conserva tal cual durante toda su reconciliación.
const PLACEHOLDER_BOOKMARK_DOM_ATTR = 'data-placeholder-bookmark';
const PlaceholderBookmarkAttributor = new Parchment.Attributor.Attribute(
  'placeholderBookmark',
  PLACEHOLDER_BOOKMARK_DOM_ATTR,
  { scope: Parchment.Scope.INLINE }
);
Quill.register(PlaceholderBookmarkAttributor, true);

// El atributo de arriba queda a propósito en el <span> confirmado (ver
// confirmReplacement) mientras dure la sesión del editor, para que la pila
// de deshacer siga pudiendo encontrarlo. No debe llegar así al HTML que
// sale del editor (ni el usuario ni el backend tienen que ver un wrapper
// de más envolviendo texto ya confirmado) — se desenvuelve acá, recién al
// exportar.
const stripPlaceholderBookmarkMarkers = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll(`[${PLACEHOLDER_BOOKMARK_DOM_ATTR}]`).forEach(el => {
    el.replaceWith(...el.childNodes);
  });
  return container.innerHTML;
};

// Quill exige que el documento siempre termine en "\n", y un BlockEmbed (acá,
// un template protegido) no puede aportar ese salto de línea final por sí
// mismo — así que cuando el ÚLTIMO bloque del documento es un template
// protegido, Quill agrega solo, automáticamente, un <p><br></p> vacío justo
// después (se puede ver con solo cargar el contenido, sin tocar nada). Es un
// detalle de implementación de Quill, no contenido real: si se guarda tal
// cual, cada carga posterior lo va a mostrar como una línea de más al final.
// Como Quill lo vuelve a crear solo cuando hace falta (para poder seguir
// escribiendo después del template), es seguro sacarlo antes de exportar: no
// se pierde ningún lugar donde escribir, solo se evita persistir un renglón
// que Quill mismo regenera cada vez.
const stripTrailingEmptyLineAfterProtectedTemplate = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const last = container.lastElementChild;
  if (
    last &&
    last.tagName === 'P' &&
    last.textContent.trim() === '' &&
    !last.querySelector('img, video, iframe') &&
    last.previousElementSibling &&
    last.previousElementSibling.classList.contains('template-wrapper')
  ) {
    last.remove();
  }
  return container.innerHTML;
};

const applyCustomMarkerAttrs = (delta, attrNames) => {
  const value = attrNames.join(' ');
  return delta.reduce((acc, op) => {
    if (op.attributes && op.attributes.customMarkerAttrs) {
      return acc.push(op);
    }
    return acc.insert(op.insert, { ...op.attributes, customMarkerAttrs: value });
  }, new (Quill.import('delta'))());
};

// Antes de emitir el HTML final, se revierte data-custom-marker-attrs a los
// atributos reales que el backend espera encontrar (p. ej. "req-..." como
// nombre de atributo, no como valor de otro atributo).
const expandCustomMarkerAttrs = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll(`[${CUSTOM_MARKER_ATTRS_DOM_ATTR}]`).forEach(el => {
    el.getAttribute(CUSTOM_MARKER_ATTRS_DOM_ATTR)
      .split(' ')
      .filter(Boolean)
      .forEach(name => el.setAttribute(name, ''));
    el.removeAttribute(CUSTOM_MARKER_ATTRS_DOM_ATTR);
  });
  return container.innerHTML;
};

// Embed inline opaco para marcadores SIN contenido editable real (el
// contador romano, el de acuerdos, las casillas de propósito): Quill
// borraría el <span> entero si quedara vacío y sin ningún formato
// reconocido. Se preserva el elemento original tal cual — el backend es el
// único que lee/reescribe su contenido, nunca el usuario.
//
// className es OBLIGATORIO acá aunque no se use en el HTML real: Parchment
// resuelve qué blot corresponde a un <span> genérico por tagName cuando
// ninguna clase matchea, y sin className propio esta clase pisa el registro
// GLOBAL de tagName='SPAN' — que hoy es el InlineBlot por defecto de Quill
// (blots/inline.js) — convirtiendo en embed opaco a cualquier <span> de
// TODA la aplicación, no solo a los marcadores. Con className seteado,
// Parchment solo la matchea si el nodo realmente tiene esa clase (cosa que
// nunca pasa con el HTML real del backend), así que en la práctica esta vía
// de detección queda inerte a propósito: la detección real la hace el
// matcher de portapapeles de más abajo, por atributo explícito.
const InlineEmbed = Quill.import('blots/embed');
// Debe calzar con GUARD_TEXT/el armado del constructor de blots/embed.js.
const EMBED_GUARD_TEXT = '﻿';

class CustomMarkerEmbed extends InlineEmbed {
  static create(value) {
    const temp = document.createElement('div');
    temp.innerHTML = value;
    const node = temp.firstElementChild || document.createElement('span');
    return CustomMarkerEmbed.stripEmbedGuards(node);
  }

  // El propio Embed base de Quill (blots/embed.js), al instanciarse, envuelve
  // el contenido real en un <span contenteditable="false"> más dos guardas de
  // ancho cero a los costados (para el manejo del cursor). Si `value` viene
  // de un ciclo previo de cargar→guardar, esa envoltura YA está adentro del
  // HTML guardado — sin este stripeo, cada ciclo apila una capa más.
  static stripEmbedGuards(node) {
    while (
      node.childNodes.length === 3 &&
      node.childNodes[0].nodeType === Node.TEXT_NODE &&
      node.childNodes[0].data === EMBED_GUARD_TEXT &&
      node.childNodes[2].nodeType === Node.TEXT_NODE &&
      node.childNodes[2].data === EMBED_GUARD_TEXT &&
      node.childNodes[1].nodeType === Node.ELEMENT_NODE &&
      node.childNodes[1].tagName === 'SPAN' &&
      node.childNodes[1].getAttribute('contenteditable') === 'false'
    ) {
      node.innerHTML = node.childNodes[1].innerHTML;
    }
    return node;
  }

  static value(node) {
    return node.outerHTML;
  }
}
CustomMarkerEmbed.className = 'enhanced-editor-custom-marker';
CustomMarkerEmbed.blotName = 'custom-marker';
CustomMarkerEmbed.tagName = 'SPAN';
Quill.register(CustomMarkerEmbed, true);

class TemplateContainer extends BlockEmbed {
  static create(value) {
    const node = super.create();
    let isNonDeletable = false;

    if (typeof value === 'string' && value.includes('<div')) {
      const temp = document.createElement('div');
      temp.innerHTML = value;
      const firstDiv = temp.querySelector('div');
      if (firstDiv) {
        node.setAttribute('template-id', firstDiv.getAttribute('template-id') || 'template-unnamed');
        node.innerHTML = firstDiv.innerHTML;
        if (firstDiv.hasAttribute('data-non-deletable') && firstDiv.getAttribute('data-non-deletable') !== 'false') {
          isNonDeletable = true;
        }
      }
    }

    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-editable-template', 'false');

    if (isNonDeletable) {
      node.setAttribute('data-non-deletable', 'true');
    }

    return node;
  }

  static value(node) {
    const clone = node.cloneNode(true);
    const isNonDeletable = clone.hasAttribute('data-non-deletable') && clone.getAttribute('data-non-deletable') !== 'false';

    clone.setAttribute('contenteditable', 'false');
    clone.setAttribute('data-editable-template', 'false');

    if (isNonDeletable) {
      clone.setAttribute('data-non-deletable', 'true');
    } else {
      clone.removeAttribute('data-non-deletable');
    }

    return clone.outerHTML;
  }

  formats() {
    return {
      'template-container': TemplateContainer.value(this.domNode),
      templateGroup: this.domNode.getAttribute('template-id'),
      templateProtected: this.domNode.getAttribute('data-non-deletable') === 'true' ? 'true' : 'false',
    };
  }

  html() {
    return this.domNode.outerHTML;
  }
}

TemplateContainer.blotName = 'template-container';
TemplateContainer.tagName = 'div';
TemplateContainer.className = 'template-wrapper';

Quill.register(TemplateContainer, true);

export default {
  name: 'EnhancedEditor',
  components: {
    VueEditor,
  },
  props: {
    id: { type: String, default: 'enhanced-editor' },
    value: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    stickyToolbar: { type: Boolean, default: true },
    stickyOffsetTop: { type: [Number, String], default: null },
    // template-id del template que SIEMPRE debe ser el último contenido del
    // documento (p. ej. "firmas-y-cierre"): nunca debe poder agregarse nada
    // después de él, ni siquiera la línea vacía que Quill agrega solo al
    // final (ver stripTrailingEmptyLineAfterProtectedTemplate). Vacío por
    // defecto: sin este prop, el editor no aplica ninguna restricción extra.
    closingTemplateId: { type: String, default: '' },
    // template-id del template que abre el documento (p. ej.
    // "header-informativo"): al cargar contenido, el cursor arranca justo
    // después de él — no importa si lo primero editable ahí es un
    // placeholder o texto normal. Vacío por defecto: sin este prop,
    // findFirstEditableIndex() cae al comportamiento genérico (saltar
    // cualquier template protegido que esté al comienzo del documento).
    headerTemplateId: { type: String, default: '' },
  },
  data() {
    return {
      quillInstance: null,
      isInternalChange: false,
    };
  },
  computed: {
    customToolbar() {
      return [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['clean'],
      ];
    },
  },
  watch: {
    value(newValue) {
      if (!this.quillInstance || this.isInternalChange) return;
      this.setEditorContent(newValue);
    },
  },
  mounted() {
    this.updateStickyOffset();
    window.addEventListener('resize', this.updateStickyOffset);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateStickyOffset);

    // El único listener que sobrevive al desmontaje del editor es este: vive
    // en "document", no en quill.root, así que no se limpia solo con el DOM.
    // Sin este removeEventListener, cada vez que el componente se
    // monta/desmonta (típico de un editor dentro de un modal reutilizable)
    // queda un listener acumulado para siempre.
    if (this.placeholderUndoKeydownHandler) {
      document.removeEventListener('keydown', this.placeholderUndoKeydownHandler, true);
      this.placeholderUndoKeydownHandler = null;
    }
    if (this.quillInstance && this._hoverMouseOverHandler) {
      this.quillInstance.root.removeEventListener('mouseover', this._hoverMouseOverHandler);
      this.quillInstance.root.removeEventListener('mouseleave', this._hoverMouseLeaveHandler);
      this._hoverMouseOverHandler = null;
      this._hoverMouseLeaveHandler = null;
    }
    this.quillInstance = null;
    this.templateGroupStyleEl = null;
  },
  methods: {
    onEditorReady(quill) {
      this.quillInstance = quill;

      // 1. Configuración del Clipboard Matcher
      // Bifurca según contenteditable: los templates no editables son un embed
      // opaco único (TemplateContainer, BlockEmbed). Los editables se convierten en
      // contenido Quill normal (bloques nativos p, h1-h6, li, blockquote), marcado
      // con el Attributor `templateGroup` (y `templateProtected` si data-non-deletable="true")
      // para poder recibir todos los estilos aplicados desde el toolbar de Quill.
      const clipboard = quill.getModule('clipboard');
      clipboard.addMatcher('div[template-id]', (node, delta) => {
        const isEditable =
          node.getAttribute('contenteditable') === 'true' ||
          node.getAttribute('data-editable-template') === 'true';

        // Si NO es editable, es un BlockEmbed único e indivisible (1 solo nodo HTML)
        if (!isEditable) {
          const Delta = Quill.import('delta');
          return new Delta().insert({
            'template-container': node.outerHTML,
          });
        }

        // Si ES editable, se descompone en bloques nativos Quill para soporte total de toolbar
        const templateId = node.getAttribute('template-id') || 'template-unnamed';
        const isNonDeletable =
          node.hasAttribute('data-non-deletable') && node.getAttribute('data-non-deletable') !== 'false';
        return applyTemplateGroup(delta, templateId, isNonDeletable);
      });

      // Preserva los marcadores personalizados del backend (ver comentario
      // arriba de OPAQUE_MARKER_ATTR_PATTERNS) en cualquier elemento del
      // documento, no solo dentro de templates — corre para cada nodo, pero
      // no hace nada salvo que encuentre alguno de esos atributos.
      clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        if (hasMatchingAttr(node, OPAQUE_MARKER_ATTR_PATTERNS)) {
          const Delta = Quill.import('delta');
          return new Delta().insert({ 'custom-marker': node.outerHTML });
        }

        const editableMarkerAttrs = getMatchingAttrNames(node, EDITABLE_MARKER_ATTR_PATTERNS);
        if (editableMarkerAttrs.length > 0) {
          return applyCustomMarkerAttrs(delta, editableMarkerAttrs);
        }

        return delta;
      });

      // Evalúa si un nodo DOM tiene la propiedad protegida
      const isProtectedElement = (domNode) => {
        if (!domNode) return false;
        const wrapper = domNode.closest ? domNode.closest('.template-wrapper') : null;
        const target = wrapper || domNode;
        return (
          target.hasAttribute &&
          target.hasAttribute('data-non-deletable') &&
          target.getAttribute('data-non-deletable') !== 'false'
        );
      };

      const getFormats = (l) => {
        if (!l || typeof l.formats !== 'function') return {};
        return l.formats() || {};
      };

      // Helpers del template de cierre (closingTemplateId) definidos como
      // métodos del componente (ver findClosingTemplateEndIndex,
      // isAtOrAfterClosingTemplate, enforceClosingTemplateIsLast más abajo)
      // para poder reutilizarlos también desde setEditorContent, que corre
      // fuera de este closure.
      const isAtOrAfterClosingTemplate = (index) => this.isAtOrAfterClosingTemplate(index);

      // 2. Evaluador de eliminación protegida
      const isDeletingProtectedTemplate = (range, keyType) => {
        if (!range) return false;

        let index = range.index;
        let length = range.length;

        if (length === 0) {
          // Si el cursor está directamente sobre un elemento protegido
          const [directLeaf] = quill.getLeaf(index);
          if (directLeaf && directLeaf.domNode && isProtectedElement(directLeaf.domNode)) {
            return true;
          }

          if (keyType === 'Backspace') {
            if (index === 0) return false;
            index = index - 1;
            length = 1;
          } else if (keyType === 'Delete') {
            const [curLine, curOffset] = quill.getLine(index);
            if (curLine && curOffset >= curLine.length() - 1) {
              // En Delete al final de la línea, el objetivo es el inicio de la siguiente línea
              index = quill.getIndex(curLine) + curLine.length();
            }
            length = 1;
          }
        }

        // 1. Si la selección abarca un BlockEmbed protegido (.template-wrapper con data-non-deletable="true")
        const [leaf] = quill.getLeaf(index);
        if (leaf && leaf.domNode && isProtectedElement(leaf.domNode)) {
          return true;
        }

        const lines = quill.getLines(index, length);
        const embedProtected = lines.some(line => line && line.domNode && isProtectedElement(line.domNode));
        if (embedProtected) return true;

        // 2. Para bloques editables con templateProtected="true":
        if (length > 0) {
          const protectedLines = lines.filter(line => {
            const f = getFormats(line);
            return f.templateProtected === 'true' || isProtectedElement(line && line.domNode);
          });
          if (protectedLines.length > 0) {
            const docLength = quill.getLength();
            if (length >= docLength - 1) return true;

            for (const pLine of protectedLines) {
              const pLineIndex = quill.getIndex(pLine);
              const pLineLength = pLine.length();
              if (index <= pLineIndex && (index + length) >= (pLineIndex + pLineLength)) {
                return true;
              }
            }
          }
        }

        return false;
      };

      // 3. Intercepción de teclado y blindaje de límites
      const keyboard = quill.getModule('keyboard');

      // A. Enter (13) al final de la última línea de un template
      if (!keyboard.bindings[13]) keyboard.bindings[13] = [];
      keyboard.bindings[13].unshift({
        key: 13,
        handler: (range) => {
          if (!range) return true;

          // El template de cierre (closingTemplateId) siempre debe ser el
          // último contenido: Enter ahí (o después) no debe multiplicar
          // líneas vacías.
          if (isAtOrAfterClosingTemplate(range.index)) {
            return false;
          }

          // Si hay una selección que abarca un template protegido, bloquear Enter
          if (range.length > 0 && isDeletingProtectedTemplate(range, 'Delete')) {
            return false;
          }

          const [line, offset] = quill.getLine(range.index);
          if (!line) return true;

          const lineFormats = getFormats(line);
          const templateId = lineFormats.templateGroup || null;
          if (!templateId) return true;

          // Verificar si es la última línea del template en el documento
          const lineIndex = quill.getIndex(line);
          const nextIndex = lineIndex + line.length();
          const [nextLine] = nextIndex < quill.getLength() ? quill.getLine(nextIndex) : [null];
          const nextFormats = getFormats(nextLine);
          const isLastLineOfTemplate =
            !nextLine ||
            nextFormats.templateGroup !== templateId;

          // Si el cursor está al final de la línea actual
          const isAtEndOfLine = offset + (range.length || 0) >= line.length() - 1;

          if (isLastLineOfTemplate && isAtEndOfLine) {
            if (range.length > 0) {
              quill.deleteText(range.index, range.length, 'user');
            }
            // Insertar salto de línea
            quill.insertText(range.index, '\n', 'user');

            // Asegurar que la nueva línea NO tenga atributos de template ni clases nuestras
            const nextLineIndex = range.index + 1;
            quill.formatLine(nextLineIndex, 1, 'templateGroup', false, 'user');
            quill.formatLine(nextLineIndex, 1, 'templateProtected', false, 'user');
            quill.formatLine(nextLineIndex, 1, 'header', false, 'user');
            quill.formatLine(nextLineIndex, 1, 'list', false, 'user');
            quill.formatLine(nextLineIndex, 1, 'blockquote', false, 'user');

            const [newLine] = quill.getLine(nextLineIndex);
            if (newLine && newLine.domNode) {
              newLine.domNode.removeAttribute('data-template-id');
              newLine.domNode.removeAttribute('data-template-non-deletable');
              newLine.domNode.classList.remove(
                'template-group-first',
                'template-group-middle',
                'template-group-last',
                'template-group-single',
                'template-group-hover'
              );
            }

            quill.setSelection(nextLineIndex, 0, 'user');
            this.syncTemplateGroupStyles();
            return false;
          }
          return true;
        },
      });

      // B. Backspace (8)
      if (!keyboard.bindings[8]) keyboard.bindings[8] = [];
      keyboard.bindings[8].unshift({
        key: 8,
        handler: (range) => {
          if (!range) return true;
          const { index, length } = range;

          // Si hay una selección con rango
          if (length > 0) {
            if (isDeletingProtectedTemplate(range, 'Backspace')) {
              return false;
            }
            return true;
          }

          // Cursor simple en index === 0
          if (index === 0) {
            const [line] = quill.getLine(0);
            const lineFormats = getFormats(line);
            const curTemplateId = lineFormats.templateGroup || null;
            const isProtected = lineFormats.templateProtected === 'true' || isProtectedElement(line && line.domNode);
            const isEmptyLine = line && line.length() <= 1 && (!line.domNode.textContent || line.domNode.textContent.trim() === '');
            if (curTemplateId && !isProtected && isEmptyLine) {
              if (quill.getLength() > 1) {
                quill.deleteText(0, 1, 'user');
                this.syncTemplateGroupStyles();
                return false;
              } else {
                quill.formatLine(0, 1, 'templateGroup', false, 'user');
                quill.formatLine(0, 1, 'templateProtected', false, 'user');
                if (line.domNode) {
                  line.domNode.removeAttribute('data-template-id');
                  line.domNode.removeAttribute('data-template-non-deletable');
                }
                this.syncTemplateGroupStyles();
                return false;
              }
            }
            return true;
          }

          const [line, offset] = quill.getLine(index);
          if (!line) return true;

          // Caso: Cursor al inicio de una línea (offset === 0)
          if (offset === 0) {
            const [prevLine] = quill.getLine(index - 1);
            const lineFormats = getFormats(line);
            const prevFormats = getFormats(prevLine);
            const curTemplateId = lineFormats.templateGroup || null;
            const prevTemplateId = prevFormats.templateGroup || null;
            const isProtected = lineFormats.templateProtected === 'true' || isProtectedElement(line && line.domNode);
            const isPrevProtected = prevFormats.templateProtected === 'true' || isProtectedElement(prevLine && prevLine.domNode);
            const isEmptyLine = line.length() <= 1 && (!line.domNode.textContent || line.domNode.textContent.trim() === '');

            // 1. Si la línea actual está VACÍA y NO está protegida:
            // El usuario está borrando esta línea vacía (sea de un template editable o un párrafo custom libre).
            // Eliminar esta línea vacía y posicionar el cursor al final de la línea anterior.
            if (!isProtected && isEmptyLine && prevLine) {
              const prevLineIndex = quill.getIndex(prevLine);
              const targetIndex = Math.max(0, prevLineIndex + prevLine.length() - 1);
              quill.deleteText(index, 1, 'user');
              quill.setSelection(targetIndex, 0, 'user');
              this.syncTemplateGroupStyles();
              return false;
            }

            // 2. Si la línea actual TIENE TEXTO y la anterior es protegida:
            // Bloquear fusión de texto hacia el template protegido para no dañarlo
            if (isPrevProtected) {
              return false;
            }

            // 3. Si la línea actual está dentro de un template con texto pero la anterior no (o de otro template):
            // Mover el cursor a la línea anterior sin fusionar para no absorber texto
            if (curTemplateId && prevTemplateId !== curTemplateId) {
              const prevLineIndex = prevLine ? quill.getIndex(prevLine) : 0;
              const targetIndex = prevLine ? prevLineIndex + prevLine.length() - 1 : 0;
              quill.setSelection(targetIndex, 0, 'user');
              return false;
            }

            // 4. Si la línea actual NO tiene template pero la anterior sí (y tiene texto):
            if (!curTemplateId && prevTemplateId) {
              const prevLineIndex = quill.getIndex(prevLine);
              const targetIndex = prevLineIndex + prevLine.length() - 1;
              quill.setSelection(targetIndex, 0, 'user');
              return false;
            }
          }

          if (isDeletingProtectedTemplate(range, 'Backspace')) {
            return false;
          }

          return true;
        },
      });

      // C. Delete (46)
      if (!keyboard.bindings[46]) keyboard.bindings[46] = [];
      keyboard.bindings[46].unshift({
        key: 46,
        handler: (range) => {
          if (!range) return true;
          const { index, length } = range;

          // Si hay selección con rango
          if (length > 0) {
            if (isDeletingProtectedTemplate(range, 'Delete')) {
              return false;
            }
            return true;
          }

          // Cursor simple
          const [line, offset] = quill.getLine(index);
          if (!line) return true;

          const lineFormats = getFormats(line);
          const curTemplateId = lineFormats.templateGroup || null;
          const isProtected = lineFormats.templateProtected === 'true' || isProtectedElement(line && line.domNode);
          const isEmptyLine = line.length() <= 1 && (!line.domNode.textContent || line.domNode.textContent.trim() === '');

          // Si la línea actual está en un template vacío no protegido y se presiona Delete en ella
          if (curTemplateId && !isProtected && isEmptyLine) {
            quill.deleteText(index, 1, 'user');
            this.syncTemplateGroupStyles();
            return false;
          }

          // Cursor al final de la línea actual
          if (offset >= line.length() - 1) {
            const lineIndex = quill.getIndex(line);
            const nextIndex = lineIndex + line.length();
            const [nextLine] = nextIndex < quill.getLength() ? quill.getLine(nextIndex) : [null];
            const nextFormats = getFormats(nextLine);
            const nextTemplateId = nextFormats.templateGroup || null;
            const isNextProtected = nextFormats.templateProtected === 'true' || isProtectedElement(nextLine && nextLine.domNode);
            const isNextEmpty = nextLine && nextLine.length() <= 1 && (!nextLine.domNode.textContent || nextLine.domNode.textContent.trim() === '');

            // Si la siguiente línea es protegida, no borrar hacia ella
            if (isNextProtected) {
              return false;
            }

            // Si la siguiente línea es un template vacío no protegido, borrarlo con Delete
            if (nextTemplateId && !isNextProtected && isNextEmpty) {
              quill.deleteText(nextIndex, 1, 'user');
              this.syncTemplateGroupStyles();
              return false;
            }

            // Si la línea actual tiene template y la siguiente es una línea libre vacía
            if (curTemplateId && !nextTemplateId && nextLine && isNextEmpty) {
              quill.deleteText(nextIndex, 1, 'user');
              this.syncTemplateGroupStyles();
              return false;
            }

            // Si cruzan frontera de templates con contenido, bloquear fusión
            if (curTemplateId !== nextTemplateId) {
              return false;
            }
          }

          if (isDeletingProtectedTemplate(range, 'Delete')) {
            return false;
          }

          return true;
        },
      });

      // 3.1. Preservación de atributos de plantilla en el botón "Limpiar formato" (ql-clean)
      const toolbar = quill.getModule('toolbar');
      if (toolbar) {
        toolbar.addHandler('clean', () => {
          const range = quill.getSelection();
          if (!range) return;

          const lines = quill.getLines(range.index, range.length || 1);
          const templateData = lines.map(line => ({
            line,
            templateGroup: line.formats().templateGroup,
            templateProtected: line.formats().templateProtected,
          }));

          quill.removeFormat(range.index, range.length, 'user');

          templateData.forEach(({ line, templateGroup, templateProtected }) => {
            if (templateGroup && line.domNode && line.domNode.isConnected) {
              const lineIndex = quill.getIndex(line);
              quill.formatLine(lineIndex, 1, 'templateGroup', templateGroup, 'silent');
              if (templateProtected) {
                quill.formatLine(lineIndex, 1, 'templateProtected', templateProtected, 'silent');
              }
            }
          });

          this.syncTemplateGroupStyles();
        });
      }

      // 4. Bloqueo de operaciones que puedan eliminar o sobrescribir un template protegido
      const preventProtectedDeletion = (e) => {
        const range = quill.getSelection();
        if (range && range.length > 0 && isDeletingProtectedTemplate(range, 'Delete')) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      quill.root.addEventListener('cut', preventProtectedDeletion);
      quill.root.addEventListener('paste', preventProtectedDeletion, true);
      quill.root.addEventListener('drop', preventProtectedDeletion, true);
      quill.root.addEventListener('beforeinput', preventProtectedDeletion);

      // 4.1. El template de cierre (closingTemplateId) siempre debe ser el
      // último contenido del documento: nunca debe poder agregarse nada
      // nuevo en o después de él (tipear, pegar, soltar un drag). El binding
      // de Enter de más arriba ya cubre el caso de multiplicar líneas
      // vacías; esto cubre cualquier otra forma de insertar contenido.
      // Backspace/Delete se dejan pasar libremente (no pasan por
      // beforeinput con un inputType que empiece en "delete" desde acá, y
      // paste/drop no tienen inputType) para poder limpiar contenido viejo
      // que haya quedado ahí de antes de existir esta regla.
      const preventContentAfterClosingTemplate = (e) => {
        if (!this.closingTemplateId) return;
        if (e.inputType && e.inputType.startsWith('delete')) return;
        const range = quill.getSelection();
        if (!range) return;
        if (isAtOrAfterClosingTemplate(range.index)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      quill.root.addEventListener('beforeinput', preventContentAfterClosingTemplate);
      quill.root.addEventListener('paste', preventContentAfterClosingTemplate, true);
      quill.root.addEventListener('drop', preventContentAfterClosingTemplate, true);

      // 5. Listener de eventos nativos de edición interna (templates
      // protegidos: siguen siendo un nodo DOM real con .template-wrapper)
      quill.root.addEventListener('input', (e) => {
        const wrapper = e.target.closest('.template-wrapper');
        if (wrapper) {
          this.$emit('wrapper-html-changed', wrapper.getAttribute('template-id'));
        }
      });

      // Mismo evento, para templates editables: ya no son un nodo DOM
      // especial, así que se detectan a nivel Delta vía el atributo
      // templateGroup que dejó el clipboard matcher en cada línea afectada.
      quill.on('text-change', (delta, _oldDelta, source) => {
        if (source !== 'user') return;
        const changedTemplateIds = new Set(
          delta.ops.map(op => op.attributes && op.attributes.templateGroup).filter(Boolean)
        );
        changedTemplateIds.forEach(templateId => this.$emit('wrapper-html-changed', templateId));
      });

      // 5.2. Garantía de fondo para el template de cierre (closingTemplateId):
      // los bloqueos preventivos de más arriba (Enter, beforeinput/paste/drop)
      // cubren tipear/pegar/soltar directamente, pero no todos los caminos por
      // los que un navegador puede terminar insertando contenido en un
      // contenteditable pasan de forma confiable por esos eventos —
      // autocompletado, el corrector ortográfico reemplazando una palabra,
      // ciertos flujos de IME. Esta es la garantía real: después de
      // CUALQUIER cambio hecho por el usuario, si quedó algo más allá de la
      // única línea vacía que Quill exige tras un BlockEmbed final, se borra
      // de inmediato — sin importar por qué medio haya llegado. No corre
      // para cambios con otro `source` (carga inicial, restauración de un
      // placeholder, etc.): no se toca contenido viejo que ya estuviera ahí
      // de antes de esta regla salvo que el usuario mismo edite algo.
      quill.on('text-change', (_delta, _oldDelta, source) => {
        if (source !== 'user') return;
        this.enforceClosingTemplateIsLast('user');
      });

      // 5.3. El cursor NUNCA debe poder quedar parado en o después del
      // template de cierre — ni por click, ni por flechas del teclado, ni
      // por foco programático. selection-change de Quill se dispara para
      // CUALQUIERA de esos caminos (a diferencia de beforeinput/keydown,
      // que solo cubren edición), así que es el único lugar confiable para
      // esta regla. Si el cursor termina ahí, se lo devuelve a la posición
      // válida donde estaba antes; si no había una posición previa válida,
      // se lo deja justo antes del template de cierre.
      quill.on('selection-change', (range) => {
        if (!range || !this.closingTemplateId) return;
        if (!this.isAtOrAfterClosingTemplate(range.index)) return;
        const endIndex = this.findClosingTemplateEndIndex();
        quill.setSelection(Math.max(0, endIndex - 1), 0, 'silent');
      });

      // 5.1. Hover colectivo para bloques editables de un mismo template
      this._hoveredTemplateId = null;
      this._hoverMouseOverHandler = (e) => {
        const target = e.target.closest ? e.target.closest('[data-template-id]') : null;
        const newHoveredId = target ? target.getAttribute('data-template-id') : null;
        if (this._hoveredTemplateId !== newHoveredId) {
          if (this._hoveredTemplateId) {
            quill.root.querySelectorAll(`[data-template-id="${CSS.escape(this._hoveredTemplateId)}"]`).forEach(el => {
              el.classList.remove('template-group-hover');
            });
          }
          if (newHoveredId) {
            quill.root.querySelectorAll(`[data-template-id="${CSS.escape(newHoveredId)}"]`).forEach(el => {
              el.classList.add('template-group-hover');
            });
          }
          this._hoveredTemplateId = newHoveredId;
        }
      };
      this._hoverMouseLeaveHandler = () => {
        if (this._hoveredTemplateId) {
          quill.root.querySelectorAll(`[data-template-id="${CSS.escape(this._hoveredTemplateId)}"]`).forEach(el => {
            el.classList.remove('template-group-hover');
          });
          this._hoveredTemplateId = null;
        }
      };
      quill.root.addEventListener('mouseover', this._hoverMouseOverHandler);
      quill.root.addEventListener('mouseleave', this._hoverMouseLeaveHandler);

      // 6. Click en un placeholder pendiente
      const isPlaceholderEditable = (node) => {
        const wrapper = node.closest('.template-wrapper');
        // Dentro de un template-container (BlockEmbed), es no editable
        if (wrapper) return false;
        return true;
      };

      // Reconstruye el embed protegido de un placeholder en el lugar donde
      // hoy está `bookmarkNode`, pasando por la API de Delta de Quill
      // (deleteText + insertEmbed) — nunca por asignación cruda de
      // outerHTML. Un placeholder "restaurado" con outerHTML nunca vuelve a
      // ser reconocido como el embed protegido: Quill arma CustomMarkerEmbed
      // únicamente al parsear HTML entrante (pegado/carga inicial), no al
      // reconciliar una mutación de DOM insertada a mano — quedaba como
      // texto editable común, desprotegido, y cada restauración sucesiva le
      // sumaba una capa más de guardas internas de Quill (contenteditable
      // anidados) hasta corromper visiblemente el texto.
      const restorePlaceholderEmbed = (bookmarkNode, embedHtml) => {
        if (!bookmarkNode || !bookmarkNode.isConnected) return;
        const blot = Parchment.find(bookmarkNode);
        if (blot) {
          const index = quill.getIndex(blot);
          const length = Math.max(typeof blot.length === 'function' ? blot.length() : 1, 1);
          quill.deleteText(index, length, 'silent');
          quill.insertEmbed(index, 'custom-marker', embedHtml, 'silent');
          return;
        }
        // Defensivo: si por timing Quill todavía no registró el bookmark
        // como blot (recién insertado, antes de que el MutationObserver lo
        // procese), se cae al reemplazo DOM directo — peor, pero mejor que
        // perder el placeholder por completo.
        bookmarkNode.outerHTML = embedHtml;
      };

      // Pila de deshacer propia para los reemplazos de placeholder, viva
      // durante toda la sesión del editor. Cada entrada apunta al span
      // CONCRETO que reemplazó a un placeholder puntual (no a una foto del
      // padre completo): si dos placeholders comparten el mismo padre,
      // deshacer uno no toca para nada al otro, sin importar cuál se editó
      // primero.
      const placeholderUndoStack = [];
      let activePlaceholderCut = null;

      const undoLastPlaceholderChange = () => {
        if (activePlaceholderCut) {
          // Todavía hay un corte en curso (no se confirmó): cancelarlo.
          activePlaceholderCut.cancel();
          return true;
        }

        const lastEntry = placeholderUndoStack.pop();
        if (!lastEntry) return false;
        if (!lastEntry.wrapper.isConnected) return false;

        restorePlaceholderEmbed(lastEntry.wrapper, lastEntry.originalHtml);
        return true;
      };

      // Ctrl+Z (o Cmd+Z): un único listener para toda la vida del editor, no
      // uno nuevo por cada click. El corte/reemplazo se hace con DOM
      // directo, no con la API de Delta de Quill, así que el historial de
      // undo de Quill no tiene registro de esto — se maneja acá en vez de
      // depender de él. Se registra en "document" (no en quill.root) con
      // capture:true: Quill ya tiene su propio manejo de teclado registrado
      // en quill.root desde antes, y para el elemento que es el target del
      // evento, el orden entre listeners no depende de capture/bubble sino
      // de cuál se registró primero — como el de Quill ya estaba ahí,
      // siempre correría antes que el nuestro si escucháramos en el mismo
      // quill.root. Escuchando en "document" con captura, en cambio,
      // corremos en la fase de captura ANTES de que el evento baje hasta
      // quill.root, así que llegamos primero sin importar el orden de
      // registro. Si no hay nada nuestro para deshacer, se deja pasar el
      // evento para que Quill haga su undo normal.
      // Guardado en la instancia (no en una variable local) para poder
      // removerlo con la misma referencia en beforeDestroy: al vivir en
      // "document" y no en quill.root, sobrevive al desmontaje del editor
      // si nadie lo saca explícitamente.
      this.placeholderUndoKeydownHandler = (event) => {
        const isUndo =
          (event.ctrlKey || event.metaKey) &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'z';
        if (!isUndo) return;
        if (!quill.root.contains(document.activeElement)) return;

        const handled = undoLastPlaceholderChange();
        if (handled) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      };
      document.addEventListener('keydown', this.placeholderUndoKeydownHandler, true);

      // Reemplazo de placeholder por selección: en vez de "cortar" el
      // placeholder (que desaparece de golpe, dejando un cursor colapsado
      // en un marcador invisible) y esperar un timer para decidir si se
      // restaura, se lo reemplaza por texto plano EDITABLE con el MISMO
      // texto visible y se lo SELECCIONA de entrada — el usuario ve
      // exactamente qué va a perder si escribe, como seleccionar cualquier
      // texto en cualquier editor. Escribir reemplaza la selección al
      // toque (comportamiento nativo del navegador, sin código extra); no
      // escribir y sacar el cursor de ahí restaura el placeholder de
      // inmediato — no hace falta esperar nada.
      const beginPlaceholderReplacement = (placeholderNode) => {
        // Si ya había un reemplazo activo (el usuario clickeó otro
        // placeholder antes de terminar con el anterior), lo cerramos ya
        // mismo: nunca hay más de uno "vivo" al mismo tiempo.
        if (activePlaceholderCut) {
          activePlaceholderCut.finishNow();
        }

        const originalPlaceholderHtml = CustomMarkerEmbed.value(placeholderNode);
        const displayText = placeholderNode.textContent.split(EMBED_GUARD_TEXT).join('');

        const bookmark = document.createElement('span');
        bookmark.setAttribute('data-placeholder-bookmark', 'true');
        bookmark.textContent = displayText || ' ';
        placeholderNode.replaceWith(bookmark);

        quill.root.focus();

        const range = document.createRange();
        range.selectNodeContents(bookmark);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        let hasTyped = false;
        let done = false;

        const isSelectionInsideBookmark = () => {
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return false;
          const anchor = sel.anchorNode;
          return !!anchor && (anchor === bookmark || bookmark.contains(anchor));
        };

        const cleanupListeners = () => {
          quill.root.removeEventListener('input', handleInput);
          document.removeEventListener('selectionchange', handleSelectionChange);
          quill.root.removeEventListener('blur', finish);
          activePlaceholderCut = null;
        };

        const confirmReplacement = () => {
          // OJO: a propósito NO se saca el atributo data-placeholder-bookmark
          // acá. Es lo único que hace que Quill reconozca este <span> como un
          // blot con formato real (ver PlaceholderBookmarkAttributor) — sin
          // él, la reconciliación interna de Quill (asíncrona, corre sola en
          // cuanto pasa un instante) lo desenvuelve por considerarlo un
          // wrapper vacío/redundante, y el nodo que acabamos de apilar más
          // abajo deja de estar en el documento antes de que el usuario
          // llegue a presionar Ctrl+Z — el síntoma era exactamente "el
          // placeholder pierde la protección" después de deshacer. Se limpia
          // recién al exportar el HTML (ver stripPlaceholderBookmarkMarkers),
          // nunca acá en vivo.
          //
          // Se apila el span concreto (no una foto del padre): deshacerlo
          // más adelante con Ctrl+Z solo va a tocar este reemplazo puntual,
          // sin importar qué otros placeholders vecinos se editen mientras
          // tanto.
          placeholderUndoStack.push({ wrapper: bookmark, originalHtml: originalPlaceholderHtml });
        };

        const finish = () => {
          if (done) return;
          done = true;
          cleanupListeners();

          if (hasTyped) {
            confirmReplacement();
          } else {
            // No se escribió nada: se restaura el placeholder original.
            restorePlaceholderEmbed(bookmark, originalPlaceholderHtml);
          }
        };

        const handleInput = () => {
          // Si el borrado dejó el bookmark completamente vacío sin que el
          // usuario haya escrito nada propio (por ejemplo Ctrl+X en vez de
          // Backspace, que no pasa por la intercepción de teclado de más
          // abajo), se trata igual que "se fue sin tocar nada": al
          // terminar, restaura el placeholder en vez de confirmar un campo
          // vacío.
          hasTyped = !!(bookmark.isConnected && bookmark.textContent && bookmark.textContent.trim() !== '');
        };

        // Apenas la selección/cursor se va del bookmark (click en otro
        // lado, Tab, etc.) se decide de inmediato — nada de esperar un
        // timer fijo.
        const handleSelectionChange = () => {
          if (!isSelectionInsideBookmark()) finish();
        };

        // Mientras este reemplazo sigue activo (todavía no se confirmó),
        // Ctrl+Z lo cancela directamente en vez de pasar por la pila
        // (todavía no hay nada apilado para este reemplazo puntual).
        const cancel = () => {
          if (done) return;
          done = true;
          cleanupListeners();
          restorePlaceholderEmbed(bookmark, originalPlaceholderHtml);
        };

        activePlaceholderCut = { cancel, finishNow: finish, bookmark };

        quill.root.addEventListener('input', handleInput);
        document.addEventListener('selectionchange', handleSelectionChange);
        quill.root.addEventListener('blur', finish);
      };

      // Backspace/Delete justo después de clickear un placeholder (con su
      // texto todavía seleccionado, o ya vaciado del todo) NO debe dejarse
      // en manos del borrado normal de Quill: si el bookmark editable queda
      // vacío, Quill puede limpiar ese span por su cuenta (optimize de un
      // blot sin contenido) antes de que el resto del código llegue a
      // reaccionar — y un Ctrl+Z inmediato después terminaba trayendo de
      // vuelta el estado a medio confirmar (con el resaltado de "recién
      // tipeado" puesto) en vez del placeholder protegido. Se intercepta
      // ANTES que cualquier otro binding (unshift, mismo patrón que el
      // resto del archivo) y, si la selección actual cubre todo lo que
      // queda del bookmark activo, se cancela el reemplazo directamente
      // (restaura el placeholder original) sin dejar que Quill borre nada.
      //
      // El bookmark se identifica por REFERENCIA directa (activePlaceholderCut.bookmark),
      // nunca con quill.root.querySelector('[data-placeholder-bookmark]'): ese atributo
      // queda a propósito en cualquier placeholder YA CONFIRMADO (ver confirmReplacement)
      // hasta que el HTML se exporta, así que puede haber más de un elemento con ese
      // atributo en el documento a la vez. Un querySelector agarra el PRIMERO en orden de
      // documento — que podía ser un placeholder confirmado en otra parte del texto, no el
      // que el usuario está editando ahora — y terminaba reemplazando texto ya confirmado
      // (en cualquier lugar, tenga o no template) por el embed protegido del placeholder
      // activo.
      const cancelActivePlaceholderOnEmptyingDelete = (range) => {
        if (!activePlaceholderCut || !range) return false;
        const bookmarkNode = activePlaceholderCut.bookmark;
        if (!bookmarkNode || !bookmarkNode.isConnected) return false;
        const bookmarkBlot = Parchment.find(bookmarkNode);
        if (!bookmarkBlot) return false;

        const bookmarkIndex = quill.getIndex(bookmarkBlot);
        const bookmarkLength = bookmarkNode.textContent.length;
        const bookmarkEnd = bookmarkIndex + bookmarkLength;

        const coversWholeBookmark =
          bookmarkLength > 0 && range.index <= bookmarkIndex && range.index + range.length >= bookmarkEnd;
        const collapsedInsideEmptyBookmark =
          bookmarkLength === 0 && range.length === 0 && range.index >= bookmarkIndex && range.index <= bookmarkEnd;

        if (!coversWholeBookmark && !collapsedInsideEmptyBookmark) return false;

        activePlaceholderCut.cancel();
        return true;
      };

      [8, 46].forEach(code => {
        if (!keyboard.bindings[code]) keyboard.bindings[code] = [];
        keyboard.bindings[code].unshift({
          key: code,
          handler: (range) => !cancelActivePlaceholderOnEmptyingDelete(range),
        });
      });

      // Antes esto se resolvía en 'mousedown' con preventDefault() inmediato
      // apenas el click caía sobre un placeholder — pero eso bloqueaba el
      // ARRASTRE nativo del navegador para seleccionar texto si el punto de
      // inicio de la selección caía sobre uno (muy común: los templates
      // suelen empezar con una oración que ya tiene un placeholder), así
      // que a veces no se podía seleccionar el template completo. Ahora se
      // decide recién en 'mouseup', mirando si terminó habiendo una
      // selección real (arrastre, se deja intacta) o el cursor quedó
      // colapsado en el mismo lugar (click simple, se dispara el reemplazo).
      let placeholderMousedownNode = null;
      quill.root.addEventListener('mousedown', (e) => {
        placeholderMousedownNode = e.target.closest('[data-placeholder-field]');
      });
      quill.root.addEventListener('mouseup', (e) => {
        const placeholderNode = placeholderMousedownNode;
        placeholderMousedownNode = null;
        if (!placeholderNode || !placeholderNode.isConnected) return;
        if (!isPlaceholderEditable(placeholderNode)) return;

        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) return; // fue un arrastre: no tocar la selección

        e.preventDefault();
        try {
          beginPlaceholderReplacement(placeholderNode);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[placeholder-debug] error en beginPlaceholderReplacement', err);
        }
      });

      // 7. Estilo dinámico para agrupar visualmente las líneas de un mismo
      // template no protegido (ver syncTemplateGroupStyles): sin un <div>
      // real que las contenga, el CSS estático no alcanza para saber dónde
      // termina un template y empieza el siguiente.
      this.templateGroupStyleEl = document.createElement('style');
      this.$el.appendChild(this.templateGroupStyleEl);

      // 8. Carga inicial
      if (this.value) {
        this.setEditorContent(this.value);
      }

      this.$emit('ready', quill);
    },

    setEditorContent(htmlContent) {
      if (!this.quillInstance) return;

      // Si esto se dispara mientras el usuario ya está parado en el editor
      // (por ejemplo si el valor vuelve del padre un poco tarde y pisa el
      // guard de isInternalChange, o ante cualquier reset externo del
      // contenido), setContents reconstruye el documento entero y sin esto
      // el cursor cae en cualquier posición por defecto — "la pantalla y el
      // cursor se van para cualquier lado". Se guarda la selección previa y
      // se restaura (ajustada al largo del documento nuevo, por si cambió)
      // en vez de dejarla a la suerte.
      const previousSelection = this.quillInstance.getSelection();

      const clipboard = this.quillInstance.getModule('clipboard');
      const delta = clipboard.convert(htmlContent || '');
      this.quillInstance.setContents(delta, 'silent');

      // El template de cierre (closingTemplateId) siempre debe ser el
      // último contenido: si el HTML que llega a cargar ya trae algo
      // después de él (dato viejo, de antes de existir esta regla, o
      // corrupto por cualquier otro motivo), se lo recorta ACÁ, apenas se
      // carga — no solo se previene que se agregue más para adelante.
      this.enforceClosingTemplateIsLast('silent');

      if (previousSelection) {
        const maxIndex = Math.max(this.quillInstance.getLength() - 1, 0);
        const index = Math.min(previousSelection.index, maxIndex);
        const length = Math.min(previousSelection.length, maxIndex - index);
        this.quillInstance.setSelection(index, length, 'silent');
      } else {
        // No había una selección previa que preservar: esto es una carga
        // "fresca" (la inicial, típicamente). El cursor arranca siempre al
        // principio del primer elemento EDITABLE del documento — nunca
        // dentro de un template protegido inicial (como el header
        // informativo) — sea ese primer elemento un placeholder o texto
        // normal, no importa.
        this.quillInstance.setSelection(this.findFirstEditableIndex(), 0, 'silent');
      }

      this.syncTemplateGroupStyles();
    },

    // Índice, en el documento de Quill, justo DESPUÉS de la última
    // aparición del template de cierre (closingTemplateId) — sea un
    // template protegido (.template-wrapper, BlockEmbed) o uno editable
    // (líneas con templateGroup). null si ese template no está presente o
    // si el prop closingTemplateId no está configurado.
    findClosingTemplateEndIndex() {
      const quill = this.quillInstance;
      if (!quill || !this.closingTemplateId) return null;
      const escapedId = CSS.escape(this.closingTemplateId);
      const nodes = quill.root.querySelectorAll(
        `.template-wrapper[template-id="${escapedId}"], [data-template-id="${escapedId}"]`
      );
      if (!nodes.length) return null;
      const lastNode = nodes[nodes.length - 1];
      const blot = Parchment.find(lastNode);
      if (!blot) return null;
      const index = quill.getIndex(blot);
      const length = typeof blot.length === 'function' ? blot.length() : 1;
      return index + length;
    },

    // true si `index` cae en o después del template de cierre: ahí no debe
    // poder agregarse contenido nuevo ni quedar parado el cursor.
    isAtOrAfterClosingTemplate(index) {
      const endIndex = this.findClosingTemplateEndIndex();
      return endIndex !== null && index >= endIndex;
    },

    // Recorta cualquier contenido que exista en o después del template de
    // cierre, dejando como mucho la única línea vacía que Quill exige tras
    // un BlockEmbed final. Se usa tanto al cargar contenido (setEditorContent)
    // como reactivamente ante cualquier edición del usuario (ver
    // 'text-change' en onEditorReady). Devuelve true si recortó algo.
    enforceClosingTemplateIsLast(source = 'silent') {
      const quill = this.quillInstance;
      if (!quill) return false;
      const endIndex = this.findClosingTemplateEndIndex();
      if (endIndex === null) return false;
      const docLength = quill.getLength();
      if (docLength > endIndex + 1) {
        quill.deleteText(endIndex, docLength - endIndex - 1, source);
        return true;
      }
      return false;
    },

    // Índice, en el documento de Quill, justo DESPUÉS del template de
    // encabezado (headerTemplateId) — mismo patrón que
    // findClosingTemplateEndIndex, pero para el template que abre el
    // documento (p. ej. "header-informativo"). null si ese template no está
    // presente o si el prop headerTemplateId no está configurado.
    findHeaderTemplateEndIndex() {
      const quill = this.quillInstance;
      if (!quill || !this.headerTemplateId) return null;
      const escapedId = CSS.escape(this.headerTemplateId);
      const node = quill.root.querySelector(
        `.template-wrapper[template-id="${escapedId}"], [data-template-id="${escapedId}"]`
      );
      if (!node) return null;
      const blot = Parchment.find(node);
      if (!blot) return null;
      const index = quill.getIndex(blot);
      const length = typeof blot.length === 'function' ? blot.length() : 1;
      return index + length;
    },

    // Índice del principio del primer contenido EDITABLE del documento: si
    // headerTemplateId está configurado, es justo lo que sigue a ESE
    // template puntual. Si no, cae al comportamiento genérico: saltar
    // cualquier template protegido (BlockEmbed, contenteditable="false")
    // que esté al comienzo del documento — sin importar si lo primero
    // editable después es un placeholder o texto normal.
    findFirstEditableIndex() {
      const quill = this.quillInstance;
      if (!quill) return 0;

      const headerEndIndex = this.findHeaderTemplateEndIndex();
      if (headerEndIndex !== null) return headerEndIndex;

      let index = 0;
      for (const child of Array.from(quill.root.children)) {
        const isLeadingProtectedBlock =
          child.classList &&
          child.classList.contains('template-wrapper') &&
          child.getAttribute('contenteditable') === 'false';
        if (!isLeadingProtectedBlock) break;
        const blot = Parchment.find(child);
        index += blot && typeof blot.length === 'function' ? blot.length() : 1;
      }
      return index;
    },

    onEditorInput(html) {
      this.isInternalChange = true;
      this.$emit(
        'input',
        wrapTemplateGroups(
          expandCustomMarkerAttrs(stripTrailingEmptyLineAfterProtectedTemplate(stripPlaceholderBookmarkMarkers(html)))
        )
      );
      this.syncTemplateGroupStyles();

      this.$nextTick(() => {
        this.isInternalChange = false;
      });
    },

    // Asigna clases de posicionamiento (primera, intermedia, última o única línea)
    // a los bloques de un mismo template-id para que se vean y comporten visualmente
    // como una única caja continua idéntica a .template-wrapper.
    syncTemplateGroupStyles() {
      if (!this.quillInstance) return;

      // 1. Limpiar clases de plantilla de TODOS los elementos que puedan tenerlas
      const allStyledElements = Array.from(
        this.quillInstance.root.querySelectorAll(
          '.template-group-first, .template-group-middle, .template-group-last, .template-group-single, .template-group-hover'
        )
      );
      allStyledElements.forEach(el => {
        el.classList.remove(
          'template-group-first',
          'template-group-middle',
          'template-group-last',
          'template-group-single',
          'template-group-hover'
        );
      });

      // 2. Sincronizar atributos de plantilla en contenedores de listas (<ol>, <ul>)
      // que son hijos directos de quill.root
      Array.from(this.quillInstance.root.children).forEach(child => {
        if (child.tagName === 'OL' || child.tagName === 'UL') {
          const firstLi = child.querySelector('li[data-template-id]');
          if (firstLi) {
            const templateId = firstLi.getAttribute('data-template-id');
            const isNonDeletable = firstLi.getAttribute('data-template-non-deletable') === 'true';
            child.setAttribute('data-template-id', templateId);
            if (isNonDeletable) {
              child.setAttribute('data-template-non-deletable', 'true');
            } else {
              child.removeAttribute('data-template-non-deletable');
            }
          } else {
            child.removeAttribute('data-template-id');
            child.removeAttribute('data-template-non-deletable');
          }
        }
      });

      // 3. Asignar clases de agrupamiento a los bloques directos de quill.root
      const elements = Array.from(this.quillInstance.root.children).filter(el => el.hasAttribute('data-template-id'));
      let currentGroup = [];
      let currentId = null;

      const processGroup = (group) => {
        if (!group.length) return;
        if (group.length === 1) {
          group[0].classList.add('template-group-single');
        } else {
          group[0].classList.add('template-group-first');
          for (let i = 1; i < group.length - 1; i++) {
            group[i].classList.add('template-group-middle');
          }
          group[group.length - 1].classList.add('template-group-last');
        }
      };

      elements.forEach(el => {
        const id = el.getAttribute('data-template-id');
        const isAdjacent = currentGroup.length > 0 && el.previousElementSibling === currentGroup[currentGroup.length - 1];
        if (id === currentId && isAdjacent) {
          currentGroup.push(el);
        } else {
          processGroup(currentGroup);
          currentGroup = [el];
          currentId = id;
        }
      });
      processGroup(currentGroup);
    },

    updateStickyOffset() {
      if (!this.stickyToolbar || !this.$el) return;
      let offset = '0px';
      if (this.stickyOffsetTop !== null && this.stickyOffsetTop !== undefined) {
        offset = typeof this.stickyOffsetTop === 'number' ? `${this.stickyOffsetTop}px` : this.stickyOffsetTop;
      } else {
        const fixedNav = document.querySelector('.navbar.fixed-top, .custom-navbar');
        if (fixedNav) {
          offset = `${fixedNav.offsetHeight || 85}px`;
        }
      }
      this.$el.style.setProperty('--editor-toolbar-top', offset);
    },
  },
};
</script>

<style scoped>
.enhanced-editor-container {
  --editor-toolbar-top: 0px;
}

.enhanced-editor-container--sticky-toolbar ::v-deep .ql-toolbar.ql-snow {
  position: sticky;
  top: var(--editor-toolbar-top, 0px);
  z-index: 100;
  background-color: #ffffff;
  border-color: #e2e2e2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

::v-deep .template-wrapper[template-id] {
  border: 1.5px dashed #dee2e6;
  padding: 1.2rem;
  margin: 1.2rem 0;
  display: block !important;
}

::v-deep .template-wrapper[template-id]:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

::v-deep .template-wrapper[template-id] *[contenteditable="true"]:focus {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
}

/* Templates editables: bloques normales de Quill agrupados visualmente como caja única */
::v-deep .ql-editor > [data-template-id] {
  border-left: 1.5px dashed #dee2e6;
  border-right: 1.5px dashed #dee2e6;
  display: block;
}

::v-deep .ql-editor > .template-group-single {
  border: 1.5px dashed #dee2e6;
  border-radius: 4px;
}

::v-deep .ql-editor > .template-group-first {
  border-top: 1.5px dashed #dee2e6;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  margin-top: 1rem;
  border-bottom: none;
}

::v-deep .ql-editor > .template-group-middle {
  border-top: none;
  border-bottom: none;
}

::v-deep .ql-editor > .template-group-last {
  border-bottom: 1.5px dashed #dee2e6;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  margin-bottom: 1rem;
  border-top: none;
}

/* Indicador de protección para bloques editables con data-template-non-deletable="true" */
::v-deep .ql-editor > [data-template-id][data-template-non-deletable="true"] {
  border-left: 4px solid #dc3545 !important;
}

/* Hover colectivo sobre toda la caja del template editable */
::v-deep .ql-editor > .template-group-hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

::v-deep .quillWrapper {
  background: #ffffff;
}

::v-deep .ql-container.ql-snow {
  min-height: 620px;
  border-color: #e2e2e2;
}

::v-deep .ql-editor {
  min-height: 620px;
  padding: 2rem 3rem;
  background: #ffffff;
  color: #11291b;
  font-size: 14px;
}

/* Indicador para plantillas protegidas no editables */
::v-deep .template-wrapper[data-non-deletable="true"] {
  user-select: none;
  border-left: 4px solid #dc3545 !important;
}

::v-deep .template-wrapper[data-non-deletable="true"] *[contenteditable="true"] {
  user-select: text;
}

/* Campos de texto pendientes de completar (marcados por backend con
   data-placeholder-field en la plantilla de minuta nueva) */
::v-deep [data-placeholder-field] {
  font-weight: bold;
  color: red;
  text-decoration: underline;
  cursor: pointer;
}

</style>